import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateRepresentative,
  useDeleteRepresentative,
  useRepresentatives,
  useUpdateRepresentative,
} from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field, Input, Select } from "@/components/ui/Input";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { PersonNameFields, suggestPersonName, type PersonNameValue } from "@/components/PersonNameFields";
import type {
  CreateRepresentativeInput,
  Representative,
  RepresentativeDocumentType,
} from "@/types";

const EMPTY: CreateRepresentativeInput = {
  firstName: "",
  secondName: "",
  firstSurname: "",
  secondSurname: "",
  documentType: "CC",
  documentNumber: "",
  email: "",
  position: "",
};

function partsOf(rep: Representative): PersonNameValue {
  if (rep.firstName && rep.firstSurname) {
    return {
      firstName: rep.firstName,
      secondName: rep.secondName ?? "",
      firstSurname: rep.firstSurname,
      secondSurname: rep.secondSurname ?? "",
    };
  }
  return suggestPersonName(rep.fullName);
}

export function RepresentativesTab({ subscriberId }: { subscriberId: string }) {
  const list = useRepresentatives(subscriberId);
  const create = useCreateRepresentative(subscriberId);
  const update = useUpdateRepresentative(subscriberId);
  const remove = useDeleteRepresentative(subscriberId);

  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState<CreateRepresentativeInput>(EMPTY);
  const [editing, setEditing] = useState<Representative | null>(null);
  const [editParts, setEditParts] = useState<PersonNameValue>({
    firstName: "",
    secondName: "",
    firstSurname: "",
    secondSurname: "",
  });

  function setF<K extends keyof CreateRepresentativeInput>(
    key: K,
    value: CreateRepresentativeInput[K]
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        ...form,
        secondName: form.secondName?.trim() || null,
        secondSurname: form.secondSurname?.trim() || null,
        email: form.email?.trim() || undefined,
      });
      toast.success("Representative added");
      setNewOpen(false);
      setForm(EMPTY);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to add representative");
    }
  }

  async function onUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    try {
      await update.mutateAsync({
        repId: editing.id,
        input: {
          firstName: editParts.firstName,
          secondName: editParts.secondName.trim() || null,
          firstSurname: editParts.firstSurname,
          secondSurname: editParts.secondSurname.trim() || null,
          email: editing.email ?? undefined,
          position: editing.position,
        },
      });
      toast.success("Representative updated");
      setEditing(null);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to update representative");
    }
  }

  async function onDelete(rep: Representative) {
    if (!confirm(`Remove ${rep.fullName}?`)) return;
    try {
      await remove.mutateAsync(rep.id);
      toast.success("Representative removed");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to remove representative");
    }
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Legal representatives</CardTitle>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </CardHeader>
      {list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.length === 0 ? (
        <EmptyState
          title="No representatives"
          description="Persona Juridica subscribers may have one or more legal representatives."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Name</TH>
              <TH>Document</TH>
              <TH>Position</TH>
              <TH>Email</TH>
              <TH className="text-right">Actions</TH>
            </tr>
          </THead>
          <TBody>
            {list.data.map((rep) => (
              <TR key={rep.id}>
                <TD className="font-medium text-slate-900">{rep.fullName}</TD>
                <TD>
                  {rep.documentType} {rep.documentNumber}
                </TD>
                <TD>{rep.position}</TD>
                <TD>{rep.email ?? <span className="text-slate-400">—</span>}</TD>
                <TD className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(rep);
                        setEditParts(partsOf(rep));
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(rep)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <Dialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        title="Add representative"
        footer={
          <>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="rep-create-form"
              loading={create.isPending}
            >
              Add
            </Button>
          </>
        }
      >
        <form id="rep-create-form" onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PersonNameFields
            value={{
              firstName: form.firstName,
              secondName: form.secondName ?? "",
              firstSurname: form.firstSurname,
              secondSurname: form.secondSurname ?? "",
            }}
            onChange={(next) =>
              setForm((f) => ({
                ...f,
                firstName: next.firstName,
                secondName: next.secondName,
                firstSurname: next.firstSurname,
                secondSurname: next.secondSurname,
              }))
            }
          />
          <Field label="Document type" required>
            <Select
              value={form.documentType}
              onChange={(e) =>
                setF("documentType", e.target.value as RepresentativeDocumentType)
              }
            >
              <option value="CC">CC</option>
              <option value="CE">CE</option>
              <option value="PASAPORTE">Pasaporte</option>
            </Select>
          </Field>
          <Field label="Document number" required>
            <Input
              value={form.documentNumber}
              onChange={(e) => setF("documentNumber", e.target.value)}
              required
              maxLength={20}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setF("email", e.target.value)}
              maxLength={256}
            />
          </Field>
          <Field label="Position" required>
            <Input
              value={form.position}
              onChange={(e) => setF("position", e.target.value)}
              required
              maxLength={200}
              placeholder="Representante Legal"
            />
          </Field>
        </form>
      </Dialog>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit representative"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button form="rep-edit-form" type="submit" loading={update.isPending}>
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <form
            id="rep-edit-form"
            onSubmit={onUpdate}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <PersonNameFields
              value={editParts}
              onChange={setEditParts}
              hint={!editing.firstName ? editing.fullName : null}
            />
            <Field label="Email">
              <Input
                type="email"
                value={editing.email ?? ""}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                maxLength={256}
              />
            </Field>
            <Field label="Position">
              <Input
                value={editing.position}
                onChange={(e) => setEditing({ ...editing, position: e.target.value })}
                maxLength={200}
              />
            </Field>
          </form>
        )}
      </Dialog>
    </Card>
  );
}
